const AFFILIATE_BROWSER_COOKIE = 'optioneer_affiliate_browser_id'
const AFFILIATE_REFERRAL_COOKIE = 'optioneer_affiliate_referral'
const AFFILIATE_COOKIE_DAYS = 30
const BROWSER_COOKIE_DAYS = 365

const BROWSER_ID_LS_KEY = 'optioneer_affiliate_browser_id'
const REFERRAL_LS_KEY = 'optioneer_affiliate_referral'

type ReferralCookiePayload = {
  referral_code: string
  captured_at: string
}

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString()
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function lsSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* localStorage unavailable */
  }
}

function createBrowserId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }

  const bytes = new Uint8Array(16)
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }

  // RFC 4122 version 4 UUID. This identifier is attribution metadata, not a
  // security token, but keep the standard shape when randomUUID is unavailable.
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function getAffiliateBrowserId(): string {
  const fromCookie = getCookie(AFFILIATE_BROWSER_COOKIE)
  if (fromCookie) return fromCookie

  const fromLS = lsGet(BROWSER_ID_LS_KEY)
  if (fromLS) {
    setCookie(AFFILIATE_BROWSER_COOKIE, fromLS, BROWSER_COOKIE_DAYS)
    return fromLS
  }

  const created = createBrowserId()
  setCookie(AFFILIATE_BROWSER_COOKIE, created, BROWSER_COOKIE_DAYS)
  lsSet(BROWSER_ID_LS_KEY, created)
  return created
}

export function storeAffiliateReferralCode(referral_code: string) {
  const payload: ReferralCookiePayload = {
    referral_code,
    captured_at: new Date().toISOString(),
  }
  const serialized = JSON.stringify(payload)
  setCookie(AFFILIATE_REFERRAL_COOKIE, serialized, AFFILIATE_COOKIE_DAYS)
  lsSet(REFERRAL_LS_KEY, serialized)
}

export function getStoredAffiliateReferral(): ReferralCookiePayload | null {
  const cookieValue = getCookie(AFFILIATE_REFERRAL_COOKIE)
  if (cookieValue) {
    try {
      return JSON.parse(cookieValue) as ReferralCookiePayload
    } catch {
      /* fall through to localStorage */
    }
  }

  const lsValue = lsGet(REFERRAL_LS_KEY)
  if (lsValue) {
    try {
      const parsed = JSON.parse(lsValue) as ReferralCookiePayload
      setCookie(AFFILIATE_REFERRAL_COOKIE, lsValue, AFFILIATE_COOKIE_DAYS)
      return parsed
    } catch {
      /* corrupted */
    }
  }

  return null
}
